'use client';

import PropTypes from 'prop-types';
import SanityImage from '@sanity-image';
import SanityAudio from 'components/helper/sanity-audio';
import styles from './exhibition-theme.module.scss';
import RichText from 'components/rich-text';
import MarkdownParser from 'components/rich-text/markdown-parser';
import ShelfSection from 'components/containers/shelf/shelf-section';
import CollectionObject from 'components/collection/collection-object';
import Accordion from 'components/accordion';
import { deburr } from 'lodash';
import LegacyOrMarkdownParser from 'components/rich-text/legacy-or-markdown-parser';

const toLabel = (name) => {
	return deburr(name.toLowerCase()).replace(/[^a-z0-9]/g, '');
};

const ExhibitionTheme = ({
	exhibitionId,
	exhibitionTitle,
	theme,
	slug,
	index,
}) => {
	const {
		title,
		subtitle,
		image,
		body,
		exhibitionMap,
		galleryID,
		audioStops,
		objectPackage,
		selectedArtworksHeading,
		accordions,
	} = theme;

	const tracks = audioStops?.filter((track) => track?.file?.asset);

	const accordionItems =
		accordions?.map(({ title, body }) => {
			return {
				heading: title,
				body: (
					<>
						<a id={toLabel(title)} />
						<RichText value={body} />
					</>
				),
			};
		}) || [];

	return (
		<section className={styles.exhibitionTheme} id={`section-${index + 1}`}>
			<div className={styles.main} data-gallery={galleryID}>
				<h2 className={styles.title}>
					<MarkdownParser>{title}</MarkdownParser>
				</h2>
				<h3 className={styles.subtitle}>
					<MarkdownParser>{subtitle}</MarkdownParser>
				</h3>
				{image && (
					<SanityImage sizes={'(max-width: 960px) 90vw, 50vw'} image={image} />
				)}
				{!!tracks?.length && (
					<SanityAudio
						exhibitionId={exhibitionId}
						exhibitionTitle={exhibitionTitle}
						options={{
							playerMode: 'mini-player',
							transcriptDefaultClosed: true,
						}}
						darkMode={true}
						tracks={tracks}
					/>
				)}
				<RichText value={body} />
				{!!accordionItems.length && (
					<Accordion accordionItems={accordionItems} />
				)}
			</div>
			<aside className={styles.aside}>
				{exhibitionMap && (
					<div className={styles['exhibition-theme__svg-wrapper']}>
						<LegacyOrMarkdownParser>{exhibitionMap}</LegacyOrMarkdownParser>
					</div>
				)}
			</aside>
			{!!objectPackage?.highlightedObjects?.length && (
				<ShelfSection
					className={styles.shelf}
					link={{
						url: `/exhibitions/${slug?.current}/exhibition-objects`,
						cta: 'View all',
					}}
					title={selectedArtworksHeading}
				>
					{objectPackage.highlightedObjects.map((object, index) => {
						return (
							<CollectionObject
								index={index}
								key={object.objectID}
								object={object}
							/>
						);
					})}
				</ShelfSection>
			)}
		</section>
	);
};

ExhibitionTheme.propTypes = {
	exhibitionId: PropTypes.string.isRequired,
	exhibitionTitle: PropTypes.string,
	objectPackage: PropTypes.shape({
		id: PropTypes.number,
		objects: PropTypes.array,
	}),
	index: PropTypes.number.isRequired,
	theme: PropTypes.shape({
		title: PropTypes.any,
		subtitle: PropTypes.any,
		image: PropTypes.any,
		body: PropTypes.any,
		exhibitionMap: PropTypes.any,
		galleryID: PropTypes.any,
		audioStops: PropTypes.any,
		objectPackage: PropTypes.shape({
			id: PropTypes.number,
			highlightedObjects: PropTypes.array,
		}),
		selectedArtworksHeading: PropTypes.string,
		accordions: PropTypes.array,
	}),
	slug: PropTypes.shape({
		current: PropTypes.string,
	}),
};

export default ExhibitionTheme;
